import {
  Flex,
  Icon,
  Table,
  TableContainer,
  Tbody,
  Td,
  Tooltip,
  Tr,
  Typography,
  TypographyChildren,
} from '@aloft/toolkit-sui';

import { compsAffectedTooltip } from '../utils/compsAffectedTooltip.js';
import { dateOfSaleAdjTooltip } from '../utils/dateOfSaleAdjTooltip.js';
import { MonthGrouping, ZillowHomeValueIndexTrendsData } from './types.js';

function Heading({ children }: { children: TypographyChildren }) {
  return (
    <Typography casing="none" variant="tableColumnTitle">
      {children}
    </Typography>
  );
}

export const ZillowHomeValueIndexTrendsTable = ({
  isZillow,
  historical,
  projection,
}: ZillowHomeValueIndexTrendsData) => {
  const showNumCloses = !isZillow && historical.some((x) => x.numCloses !== undefined);
  const showMedianListPrice = !isZillow && historical.some((x) => x.medianListPrice !== undefined);
  const showMedianCloseOverList =
    !isZillow && historical.some((x) => x.medianClosePriceOverListPrice !== undefined);
  const showMedianDaysOnMarket =
    !isZillow && historical.some((x) => x.medianDaysOnMarket !== undefined);

  return (
    <TableContainer paddingY={2} backgroundColor="textOffset" marginY={2}>
      <Table stickyFirstColumn>
        <Tbody>
          <Row
            historical={historical}
            projection={projection}
            label=""
            isHeading
            selector={(x) => x.month}
            formatter={(x) => x}
          />
          <Row
            historical={historical}
            projection={projection}
            label={isZillow ? 'Typical Home Value' : 'Median Close Price'}
            selector={(x) => x.typicalValue}
            formatter={(x) => x.toString(true)}
            dfl="-"
          />
          {showMedianListPrice && (
            <Row
              historical={historical}
              projection={projection}
              label="Median List Price"
              selector={(x) => x.medianListPrice}
              formatter={(x) => x.toString(true)}
              dfl="-"
            />
          )}
          {showMedianCloseOverList && (
            <Row
              historical={historical}
              projection={projection}
              label="Median SP as % LP"
              selector={(x) => x.medianClosePriceOverListPrice}
              formatter={(x) => (x * 100).toFixed(1) + '%'}
              dfl="-"
            />
          )}
          {showMedianDaysOnMarket && (
            <Row
              historical={historical}
              projection={projection}
              label="Median DOM"
              selector={(x) => x.medianDaysOnMarket}
              formatter={(x) => x.toFixed(0)}
              dfl="-"
            />
          )}
          {showNumCloses && (
            <Row
              historical={historical}
              projection={projection}
              label="Num. Closed Sales"
              selector={(x) => x.numCloses}
              formatter={(x) => x.toFixed(0)}
              dfl="-"
            />
          )}
          <Row
            historical={historical}
            projection={projection}
            label="Date of Sale Adj."
            selector={(x) => x.percentChange}
            formatter={(x) => x.toFixed(2) + '%'}
            tooltip={dateOfSaleAdjTooltip(isZillow ? 'Typical Home Value' : 'Median Close Price')}
          />
          <Row
            historical={historical}
            projection={projection}
            label="Comps Affected"
            selector={(x) => x.affectedComps}
            formatter={(x) => x}
            tooltip={compsAffectedTooltip(isZillow ? 'Zillow' : 'MLS data')}
          />
        </Tbody>
      </Table>
    </TableContainer>
  );
};

type RowProps<T> = {
  historical: MonthGrouping[];
  projection?: MonthGrouping[];
  label: string;
  isHeading?: boolean;
  selector: (x: MonthGrouping) => T | undefined;
  formatter: (x: T) => string;
  dfl?: string;
  tooltip?: string;
};

function Row<T>(props: RowProps<T>) {
  const { historical, projection, label, isHeading, selector, formatter, dfl, tooltip } = props;

  const toString = (x: MonthGrouping): string => {
    const value = selector(x);
    if (value === undefined) return dfl ?? '';
    return formatter(value);
  };

  const Text = isHeading ? Heading : Typography;

  return (
    <Tr>
      <Td>
        <Flex flexDirection="row">
          <Heading>{label}</Heading>
          {tooltip !== undefined && (
            <Tooltip justifyItems={'center'} label={tooltip}>
              <Flex alignItems="center" marginLeft="5px">
                <Icon size="sm" icon={'question'} color="textSecondary" />
              </Flex>
            </Tooltip>
          )}
        </Flex>
      </Td>
      {historical.map((val, i) => (
        <Td key={i}>
          <Text>{toString(val)}</Text>
        </Td>
      ))}
      {projection &&
        projection.map((val, i) => (
          <Td key={`projection-data-${i}`}>
            <Text
              __dangerDoNotUseSx={{
                textDecoration: 'underline dotted red',
              }}
            >
              {toString(val)}
            </Text>
          </Td>
        ))}
    </Tr>
  );
}
